import { useFetcher } from '@remix-run/react'
import { useMachine } from '@xstate/react'
import React, {
	createContext,
	useContext,
	useEffect,
	useCallback,
	useMemo,
	useRef,
	type ReactNode,
} from 'react'
import {
	createMachine,
	assign,
	fromCallback,
	type StateValueFrom,
} from 'xstate'
import { useSseEvent } from '#app/components/sse-provider.tsx'
import {
	type IMessage,
	type TUnreadMessages,
} from '#app/models/message._._definitions.ts'
import { fetcherResourceSuccess } from '#app/utils/fetcher-helpers.tsx'
import { createLogger } from '#app/utils/logger.ts'
import { useDebounce } from '#app/utils/misc.tsx'
import { type TRequestResponseWithNonce } from '#app/utils/request-helpers.tsx'
import { SSE_STREAM } from '#app/utils/sse.ts'
import { usePollingControl } from './polling-control'

const POLL_INTERVAL = 30000
const INITIAL_LOAD_DELAY = 5000
const DEBOUNCE_POLL_FETCH_MS = 1000
const DEBOUNCE_SSE_FETCH_MS = 500
const UNREAD_MESSAGES_URL = '/resources/unread-messages'

const umpLogger = createLogger('UnreadMessagesProvider', false)

// --- Types ---
interface UnreadMessagesContextData {
	userId: string | null
	unreadAiCount: number
	unreadAiChatIds: string[]
	unreadGroupCount: number
	unreadGroupChatIds: string[]
	errorMessage: string | null
	initialLoadDelayComplete: boolean
	isPollingHalted: boolean // Mirrored from usePollingControl
}

type UnreadMessagesEvent =
	| { type: 'PROVIDER_INIT'; payload: { userId: string | null } }
	| { type: 'USER_LOGGED_OUT' } // Explicit event for logout
	| {
			type: 'FETCH_UNREAD_SUCCESS'
			output: TUnreadMessages & Partial<TRequestResponseWithNonce>
	  }
	| { type: 'FETCH_UNREAD_ERROR'; error: string }
	| { type: 'SSE_RECEIVED' }
	| { type: 'POLLING_TICK' }
	| { type: 'REFRESH' }
	| { type: 'HALT_POLLING' } // Event triggered by usePollingControl
	| { type: 'RESUME_POLLING' } // Event triggered by usePollingControl

// --- XState Machine Definition ---

const unreadMessagesMachine = createMachine(
	{
		types: {
			context: {} as UnreadMessagesContextData,
			events: {} as UnreadMessagesEvent,
			input: {} as Pick<UnreadMessagesContextData, 'userId'>,
		},
		id: 'unreadMessages',
		context: ({ input }) => ({
			userId: input.userId,
			unreadAiCount: 0,
			unreadAiChatIds: [],
			unreadGroupCount: 0,
			unreadGroupChatIds: [],
			errorMessage: null,
			initialLoadDelayComplete: false,
			isPollingHalted: false, // Initial assumption, sync later
		}),
		initial: 'initializing',
		states: {
			initializing: {
				entry: 'logStateTransition',
				on: {
					PROVIDER_INIT: [
						{
							target: 'idle',
							guard: ({ event }) => !event.payload.userId,
							actions: [
								assign({
									userId: ({ event }) => event.payload.userId,
									unreadAiCount: 0,
									unreadGroupCount: 0,
									unreadGroupChatIds: [],
									errorMessage: null,
									initialLoadDelayComplete: false,
									isPollingHalted: false,
								}),
							],
						},
						// If user is present and polling not halted, start delay
						{
							target: 'initial_delay',
							guard: ({ context }) =>
								!!context.userId && !context.isPollingHalted,
							actions: [
								assign({
									userId: ({ event }) => event.payload.userId,
								}),
							],
						},
						// If user present but polling *is* halted, go idle
						{
							target: 'idle',
							actions: [
								assign({
									userId: ({ event }) => event.payload.userId,
								}),
							],
						},
					],
				},
			},
			idle: {
				entry: [
					'resetCountsAndError',
					assign({
						initialLoadDelayComplete: false,
					}),
					'logStateTransition',
				],
				on: {
					// If user logs in or polling resumes, start the initial delay/load process
					PROVIDER_INIT: {
						target: 'initial_delay',
						guard: ({ context, event }) =>
							!!event.payload.userId && !context.isPollingHalted,
						actions: [assign({ userId: ({ event }) => event.payload.userId })],
					},
					RESUME_POLLING: {
						target: 'initial_delay',
						guard: ({ context }) => !!context.userId, // Only resume if logged in
						actions: [assign({ isPollingHalted: false })],
					},
					USER_LOGGED_OUT: {
						actions: ['resetCountsAndError'],
					},
					HALT_POLLING: {
						actions: [assign({ isPollingHalted: true })],
					},
					// Ignore triggers relevent only when active
					POLLING_TICK: undefined,
					SSE_RECEIVED: undefined,
					REFRESH: undefined,
				},
			},
			initial_delay: {
				entry: ['logStateTransition'],
				after: {
					INITIAL_LOAD_DELAY: {
						target: 'fetching_unread_counts',
						actions: ['logDelayComplete'],
					},
				},
				on: {
					HALT_POLLING: {
						target: 'idle',
						actions: ['haltPollingAction'],
					},
					USER_LOGGED_OUT: {
						target: 'idle',
						actions: ['resetCountsAndError'],
					},
					// Ignore other events during delay
					POLLING_TICK: undefined,
					SSE_RECEIVED: undefined,
					REFRESH: undefined,
					PROVIDER_INIT: undefined,
					RESUME_POLLING: undefined,
				},
			},
			// This state triggers the GET /resources/unread-messages fetch
			fetching_unread_counts: {
				entry: ['logStateTransition'],
				on: {
					FETCH_UNREAD_SUCCESS: {
						target: 'active',
						actions: [
							'updateCountsAction',
							'markInitialLoadCompleteIfNecessary',
						],
					},
					FETCH_UNREAD_ERROR: {
						target: 'error',
						actions: ['setErrorMessage', 'logFetchError'],
					},
					HALT_POLLING: {
						target: 'idle',
						actions: ['haltPollingAction'],
					},
					USER_LOGGED_OUT: {
						target: 'idle',
						actions: ['resetCountsAndError'],
					},
					// Ignore other triggers while fetching counts
					POLLING_TICK: undefined,
					SSE_RECEIVED: undefined,
					REFRESH: undefined,
					PROVIDER_INIT: undefined,
					RESUME_POLLING: undefined,
				},
			},
			active: {
				entry: ['logStateTransition', assign({ errorMessage: null })],
				invoke: {
					id: 'pollingInterval',
					src: 'pollingIntervalService',
				},
				on: {
					POLLING_TICK: {
						target: 'fetching_unread_counts',
						guard: 'canPollFetch',
					},
					SSE_RECEIVED: {
						target: 'fetching_unread_counts',
						guard: ({ context }) => !context.isPollingHalted,
					},
					REFRESH: {
						target: 'fetching_unread_counts',
						guard: ({ context }) => !context.isPollingHalted,
					},
					HALT_POLLING: {
						target: 'idle',
						actions: ['haltPollingAction'],
					},
					USER_LOGGED_OUT: {
						target: 'idle',
						actions: ['resetCountsAndError'],
					},
					// Ignore irrelevant events
					PROVIDER_INIT: undefined,
					RESUME_POLLING: undefined,
				},
			},
			error: {
				entry: ['logStateTransition'],
				on: {
					// Allow retry on refresh, SSE, or polling resumption
					REFRESH: {
						target: 'fetching_unread_counts',
						guard: ({ context }) => !context.isPollingHalted,
						actions: [assign({ errorMessage: null })],
					},
					SSE_RECEIVED: {
						target: 'fetching_unread_counts',
						guard: ({ context }) => !context.isPollingHalted,
						actions: [assign({ errorMessage: null })],
					},
					RESUME_POLLING: {
						target: 'fetching_unread_counts',
						guard: ({ context }) => !!context.userId,
						actions: [
							assign({ isPollingHalted: false }),
							assign({ errorMessage: null }),
						],
					},
					HALT_POLLING: {
						target: 'idle',
						actions: ['haltPollingAction'],
					},
					USER_LOGGED_OUT: {
						target: 'idle',
						actions: ['resetCountsAndError'],
					},
					// Ignore polling ticks while in error state
					POLLING_TICK: undefined,
					PROVIDER_INIT: undefined,
				},
			},
		},
	},
	{
		actions: {
			logStateTransition: ({ event, self }) => {
				const stateValue = self.getSnapshot().value
				umpLogger.debug(
					`State Transition triggered by Event: ${event.type}, Current State: ${JSON.stringify(stateValue)}`,
					{},
				)
			},
			logDelayComplete: () => {
				umpLogger.debug('Initial load delay complete', {})
			},
			logFetchError: ({ event, context }) => {
				if (event.type === 'FETCH_UNREAD_ERROR') {
					umpLogger.error('Fetch Unread Error:', {
						error: event.error,
						context,
					})
				}
			},
			resetCountsAndError: assign({
				userId: null,
				unreadAiCount: 0,
				unreadAiChatIds: [],
				unreadGroupCount: 0,
				unreadGroupChatIds: [],
				errorMessage: null,
				initialLoadDelayComplete: false,
				isPollingHalted: true, // assume polling is halted on reset
			}),
			updateCountsAction: assign(
				({ event }: { event: UnreadMessagesEvent }) => {
					if (event.type !== 'FETCH_UNREAD_SUCCESS') return {}
					const data = event.output
					const aiMessages = data?.unreadAiMessages ?? []
					const aiChatIds = [...new Set(aiMessages)]
					const groupMessages = data?.unreadGroupMessages ?? []
					const unreadGroupChatIds = [...new Set(groupMessages)]
					return {
						unreadAiCount: aiChatIds.length,
						unreadAiChatIds: aiChatIds,
						unreadGroupCount: unreadGroupChatIds.length,
						unreadGroupChatIds: unreadGroupChatIds,
						errorMessage: null,
					}
				},
			),
			setErrorMessage: assign({
				errorMessage: ({ event }) => {
					if (event.type === 'FETCH_UNREAD_ERROR') {
						return String(event.error ?? 'Unknown fetch unread counts error')
					}

					return 'Unknown error'
				},
			}),
			haltPollingAction: assign({
				isPollingHalted: true,
			}),
			markInitialLoadCompleteIfNecessary: assign(({ context }) => {
				if (!context.initialLoadDelayComplete) {
					return { initialLoadDelayComplete: true }
				}
				return {}
			}),
		},
		actors: {
			pollingIntervalService: fromCallback(({ sendBack }) => {
				let pollTimeoutId: NodeJS.Timeout | null = null

				const intervalId = setInterval(() => {
					if (document.visibilityState === 'visible') {
						clearTimeout(pollTimeoutId!)
						pollTimeoutId = setTimeout(() => {
							sendBack({ type: 'POLLING_TICK' })
						}, DEBOUNCE_POLL_FETCH_MS)
					} else {
						clearTimeout(pollTimeoutId!)
					}
				}, POLL_INTERVAL)

				return () => {
					clearInterval(intervalId)
					clearTimeout(pollTimeoutId!)
				}
			}),
		},
		guards: {
			canPollFetch: ({ context }) => {
				const isActive = !context.isPollingHalted
				return isActive
			},
		},
		delays: {
			INITIAL_LOAD_DELAY,
		},
	},
)

// --- React Context ---

interface UnreadMessagesContextType {
	unreadAiCount: number
	unreadAiChatIds: string[]
	unreadGroupCount: number
	unreadGroupChatIds: string[]
	isLoading: boolean // fetching counts or marking as read
	isFetchingUnreadCounts: boolean // fetching counts
	isIdle: boolean
	error: string | null
	refresh: () => void
	refreshUnreadCounts: () => void
	haltPolling: () => void
	resumePolling: () => void
	stateValue: StateValueFrom<typeof unreadMessagesMachine>
}

const UnreadMessagesContext = createContext<UnreadMessagesContextType | null>(
	null,
)

// --- Provider Component ---

interface UnreadMessagesProviderProps {
	children: ReactNode
	userId: string | null
}

export function UnreadMessagesProvider({
	children,
	userId,
}: UnreadMessagesProviderProps) {
	const {
		isPollingHalted: isPollingControlHalted,
		haltPolling: haltPollingControl,
		resumePolling: resumePollingControl,
	} = usePollingControl()

	// --- Fetcher for GET /resources/unread-messages ---
	const unreadMessageFetcher = useFetcher<
		TUnreadMessages & Partial<TRequestResponseWithNonce>
	>()
	const unreadMessageFetchInFlightRef = useRef(false)
	const unreadMessageFetchedDataRef = useRef<TUnreadMessages | null>(null)
	const currentUnreadMessageFetcherNonceRef = useRef<string | null>(null)

	// --- SSE Handling ---
	const lastTriggeringSseIdRef = useRef<string | null>(null)
	const sseDebounceTimeoutRef = useRef<NodeJS.Timeout | null>(null)

	const [state, send] = useMachine(unreadMessagesMachine, { input: { userId } })

	// --- Fetcher Trigger Effects ---
	// Effect to trigger GET /resources/unread-messages
	useEffect(() => {
		const shouldSendFetcherRequest =
			state.matches('fetching_unread_counts') &&
			unreadMessageFetcher.state === 'idle' &&
			!unreadMessageFetchInFlightRef.current

		if (shouldSendFetcherRequest) {
			unreadMessageFetchInFlightRef.current = true
			unreadMessageFetchedDataRef.current = null

			const nonce = Date.now().toString()
			currentUnreadMessageFetcherNonceRef.current = nonce
			unreadMessageFetcher.load(`${UNREAD_MESSAGES_URL}?nonce=${nonce}`)
		}
	}, [state.value, unreadMessageFetcher.state, unreadMessageFetcher.load])

	// --- Fetcher Result Effects ---
	// Effect to handle result of GET /resources/unread-messages
	useEffect(() => {
		const expectingCompletedFetcher =
			state.matches('fetching_unread_counts') && // Ensure machine is in fetching state for unread counts
			unreadMessageFetcher.state === 'idle' && // Ensure fetcher is idle
			unreadMessageFetchInFlightRef.current // Ensure fetch was in flight

		if (!expectingCompletedFetcher) return

		// Ensure the nonce matches the current nonce
		// so that we don't process an old fetch
		const nonceMatch =
			unreadMessageFetcher.data?.nonce ===
			currentUnreadMessageFetcherNonceRef.current

		if (!nonceMatch) {
			return
		}

		if (fetcherResourceSuccess(unreadMessageFetcher)) {
			send({
				type: 'FETCH_UNREAD_SUCCESS',
				output: unreadMessageFetcher.data!,
			})

			unreadMessageFetchedDataRef.current = unreadMessageFetcher.data ?? null
		} else {
			// Handle potential fetcher errors if the fetcher doesn't return data but becomes idle
			// This might happen if the resource route throws an error response
			umpLogger.error('Unread messages fetch failed', {})
			send({
				type: 'FETCH_UNREAD_ERROR',
				error: 'Failed to fetch unread messages',
			})
		}

		unreadMessageFetchInFlightRef.current = false // Reset flag
		currentUnreadMessageFetcherNonceRef.current = null // Reset nonce
	}, [unreadMessageFetcher.state, unreadMessageFetcher.data, state.value, send])

	// --- Other Effects (User Sync, Polling Sync, SSE, etc.) ---
	// User Sync Effect
	useEffect(() => {
		if (userId === null && state.context.userId !== null) {
			send({ type: 'USER_LOGGED_OUT' })
			return
		}

		send({ type: 'PROVIDER_INIT', payload: { userId } })
	}, [userId, send, state.context.userId])

	// Polling Control Sync Effect
	useEffect(() => {
		// avoid sending redundant events during initialization
		if (state.matches('initializing')) {
			return
		}

		if (isPollingControlHalted !== state.context.isPollingHalted) {
			send({ type: isPollingControlHalted ? 'HALT_POLLING' : 'RESUME_POLLING' })
		}
	}, [isPollingControlHalted, state.context.isPollingHalted, state.value, send])

	// SSE Effect
	const newMessageSse = useSseEvent<IMessage>(SSE_STREAM.NEW_CHAT_MESSAGE)
	useEffect(() => {
		// Always clear the pending debounce timeout on re-run
		if (sseDebounceTimeoutRef.current) {
			clearTimeout(sseDebounceTimeoutRef.current)
		}

		// 1. Basic conditions: message exists, initial load done.
		if (newMessageSse?.id && state.context.initialLoadDelayComplete) {
			// 2. ID Check: Is this a genuinely *new* event ID compared to the last one that *triggered* a debounce?
			if (newMessageSse.id === lastTriggeringSseIdRef.current) {
				return // Don't set a new timeout
			}

			// 3. State Check: Is the machine ready to accept an SSE trigger?
			const sseReadyState = state.matches('active') || state.matches('error')
			// Ensure *both* fetchers are idle and machine isn't busy
			const fetchersIdle = unreadMessageFetcher.state === 'idle' && true // markChatAsReadFetcher removed
			const machineNotBusy = !state.matches('fetching_unread_counts') && true // marking_chat_as_read state removed

			if (sseReadyState && fetchersIdle && machineNotBusy) {
				// 4. Mark this ID as the one that *initiated* the current debounce sequence.
				// Do this *before* setting the timeout.
				lastTriggeringSseIdRef.current = newMessageSse.id

				// Set a new debounce timeout
				sseDebounceTimeoutRef.current = setTimeout(() => {
					send({ type: 'SSE_RECEIVED' })
				}, DEBOUNCE_SSE_FETCH_MS)
			}
		}

		return () => {
			if (sseDebounceTimeoutRef.current) {
				clearTimeout(sseDebounceTimeoutRef.current)
			}
		}
	}, [
		newMessageSse,
		state.context.initialLoadDelayComplete,
		unreadMessageFetcher.state,
		state.value,
		send,
	])

	// --- Context Actions ---
	const debouncedRefreshMachine = useDebounce(() => {
		send({ type: 'REFRESH' })
	}, 300) // 300-500ms delay as requested

	const refreshUnreadCounts = useCallback(() => {
		debouncedRefreshMachine()
	}, [debouncedRefreshMachine])

	// use polling control directly to avoid extra state updates
	const haltPolling = haltPollingControl
	const resumePolling = resumePollingControl

	// --- Derived State for Context Consumers ---
	const isFetchingUnreadCounts = state.matches('fetching_unread_counts')
	const initialDelay = state.matches('initial_delay')
	const isLoading = isFetchingUnreadCounts || initialDelay
	const isIdle = state.matches('idle')
	const error = state.context.errorMessage

	const contextValue = useMemo(
		() => ({
			unreadAiCount: state.context.unreadAiCount,
			unreadAiChatIds: state.context.unreadAiChatIds,
			unreadGroupCount: state.context.unreadGroupCount,
			unreadGroupChatIds: state.context.unreadGroupChatIds,
			isLoading,
			isFetchingUnreadCounts,
			isIdle,
			error,
			refresh: refreshUnreadCounts, // Retain 'refresh' for simple manual refresh
			refreshUnreadCounts, // Expose the specifically named debounced function
			haltPolling,
			resumePolling,
			stateValue: state.value,
		}),
		[
			state.context.unreadAiCount,
			state.context.unreadGroupCount,
			state.context.unreadGroupChatIds,
			state.context.errorMessage,
			state.value,
			isLoading,
			isFetchingUnreadCounts,
			isIdle,
			error,
			refreshUnreadCounts,
			haltPolling,
			resumePolling,
		],
	)

	return (
		<UnreadMessagesContext.Provider value={contextValue}>
			{children}
		</UnreadMessagesContext.Provider>
	)
}

// --- Consumer Hook ---
export function useUnreadMessages() {
	const context = useContext(UnreadMessagesContext)
	if (!context) {
		throw new Error(
			'useUnreadMessages must be used within an UnreadMessagesProvider',
		)
	}
	return context
}
